/**
 * Security utilities for hashing and data protection.
 *
 * Uses the Web Crypto API (browser) or Node.js crypto module (server)
 * — no hand-rolled implementations.
 */

export function normalizeEmail(email: string): string {
    if (!email || typeof email !== 'string') {
        throw new Error('Invalid email provided for hashing');
    }
    return email.trim().toLowerCase();
}

export async function hashSHA256(value: string): Promise<string> {
    if (typeof window !== 'undefined' && crypto?.subtle) {
        const data = new TextEncoder().encode(value);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        return Array.from(new Uint8Array(hashBuffer))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
    }

    const { createHash } = await import('crypto');
    return createHash('sha256').update(value).digest('hex');
}

/**
 * Normalizes and hashes an email address using SHA-256.
 * Main entry point for email hashing before sending to dataLayer.
 *
 * @example
 * const hash = await hashEmail('  John.Doe@Example.com  ');
 * // Returns: SHA-256 hash of 'john.doe@example.com'
 */
export async function hashEmail(email: string): Promise<string> {
    const normalizedEmail = normalizeEmail(email);
    return hashSHA256(normalizedEmail);
}

// E.164 attendu par l'Enhanced Conversion ; la saisie du tunnel est nationale, d'où l'indicatif France par défaut.
export function normalizePhone(phone: string): string {
    if (!phone || typeof phone !== 'string') {
        throw new Error('Invalid phone provided for hashing');
    }
    const cleaned = phone.replace(/[^\d+]/g, '');
    let e164;
    if (cleaned.startsWith('+')) {
        e164 = cleaned;
    } else if (cleaned.startsWith('00')) {
        e164 = `+${cleaned.slice(2)}`;
    } else if (cleaned.startsWith('0')) {
        e164 = `+33${cleaned.slice(1)}`;
    } else {
        e164 = `+${cleaned}`;
    }
    // « +33 (0)6 » garde le préfixe national après l'indicatif : sans ça son empreinte diffère de « 06 ».
    return e164.replace(/^\+330(?=\d)/, '+33');
}

export async function hashPhone(phone: string): Promise<string> {
    return hashSHA256(normalizePhone(phone));
}
